import React from 'react';
import { Hash, Lock, Plus, Edit2, Trash2, MoreVertical, ChevronDown, Search, FileText } from 'lucide-react';
import { ChatChannel } from '../../../services/chatService';
import { DirectMessage } from '../../../types/chat.types';
import { useAuth } from '../../../contexts/AuthContext';

interface ChannelListProps {
  channels: ChatChannel[];
  directMessages: DirectMessage[];
  currentChannel: ChatChannel | null;
  currentDM: DirectMessage | null;
  showChannelMenu: string | null;
  onChannelSelect: (channel: ChatChannel) => void;
  onDMSelect: (dm: DirectMessage) => void;
  onCreateChannel: () => void;
  onBrowseChannels: () => void;
  onChannelMenuToggle: (channelId: string | null) => void;
  onChannelEdit: (channelId: string, name: string) => void;
  onChannelDelete: (channelId: string) => void;
  onChannelDescriptionEdit: (channelId: string, description: string) => void;
  isAdmin: boolean;
}

const ChannelList: React.FC<ChannelListProps> = ({
  channels,
  directMessages,
  currentChannel,
  currentDM,
  showChannelMenu,
  onChannelSelect,
  onDMSelect,
  onCreateChannel,
  onBrowseChannels,
  onChannelMenuToggle,
  onChannelEdit,
  onChannelDelete,
  onChannelDescriptionEdit,
  isAdmin
}) => {
  const { user } = useAuth();
  const handleChannelEdit = (channel: ChatChannel) => {
    const newName = prompt('새 채널 이름을 입력하세요:', channel.name);
    if (newName && newName !== channel.name) {
      onChannelEdit(channel.id, newName);
    }
    onChannelMenuToggle(null);
  };

  const handleChannelDescriptionEdit = (channel: ChatChannel) => {
    const newDescription = prompt('채널 소개를 입력하세요:', channel.description || '');
    if (newDescription !== null) {
      onChannelDescriptionEdit(channel.id, newDescription);
    }
    onChannelMenuToggle(null);
  };

  const handleChannelDelete = (channelId: string) => {
    if (window.confirm('정말 이 채널을 삭제하시겠습니까? 모든 메시지가 삭제됩니다.')) {
      onChannelDelete(channelId);
    }
    onChannelMenuToggle(null);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Channels */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <button className="flex items-center gap-1 text-sm font-medium hover:text-white/90">
            <ChevronDown className="w-4 h-4" />
            <span>Channels</span>
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={onBrowseChannels}
              className="hover:bg-slate-700 p-1 rounded"
              title="채널 검색"
            >
              <Search className="w-4 h-4" />
            </button>
            <button
              onClick={onCreateChannel}
              className="hover:bg-slate-700 p-1 rounded"
              title="채널 추가"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="space-y-1">
          {channels.map(channel => (
            <div
              key={channel.id}
              onClick={() => onChannelSelect(channel)}
              className={`w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-700 text-left group cursor-pointer ${
                currentChannel?.id === channel.id && !currentDM ? 'bg-slate-700' : ''
              }`}
            >
              {channel.type === 'private' ? (
                <Lock className="w-4 h-4 opacity-50 flex-shrink-0" />
              ) : (
                <Hash className="w-4 h-4 opacity-50 flex-shrink-0" />
              )}
              <span className="text-sm flex-1 truncate">{channel.name}</span>

              {/* AI 활성화 표시 */}
              {channel.ai_enabled && (
                <span className="text-xs font-semibold text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded flex-shrink-0">AI</span>
              )}

              {/* 채널 메뉴 버튼 (항상 표시, admin만) */}
              {isAdmin && (
                <div className="relative flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onChannelMenuToggle(showChannelMenu === channel.id ? null : channel.id);
                    }}
                    className="p-1 hover:bg-slate-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>

                  {/* 채널 메뉴 */}
                  {showChannelMenu === channel.id && (
                    <div className="absolute right-0 top-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg py-1 z-20 w-48">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChannelEdit(channel);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-600 flex items-center gap-2 text-sm"
                      >
                        <Edit2 className="w-3 h-3" />
                        채널명 수정
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChannelDescriptionEdit(channel);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-600 flex items-center gap-2 text-sm"
                      >
                        <FileText className="w-3 h-3" />
                        채널 소개 수정
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChannelDelete(channel.id);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-600 text-red-400 flex items-center gap-2 text-sm"
                      >
                        <Trash2 className="w-3 h-3" />
                        채널 삭제
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Direct Messages */}
      <div className="p-3 border-t border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <button className="flex items-center gap-1 text-sm font-medium hover:text-white/90">
            <ChevronDown className="w-4 h-4" />
            <span>Direct Messages</span>
          </button>
        </div>
        <div className="space-y-1">
          {directMessages.map(dm => (
            <div
              key={dm.id}
              onClick={() => onDMSelect(dm)}
              className={`w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-700 text-left cursor-pointer ${
                currentDM?.id === dm.id ? 'bg-slate-700' : ''
              }`}
            >
              <div className="relative">
                <div className={`w-2 h-2 rounded-full ${dm.is_online ? 'bg-green-400' : 'bg-gray-400'}`} />
              </div>
              <span className="text-sm flex-1">@{dm.user_name}</span>
              {dm.unread_count > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {dm.unread_count}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChannelList;